import { Brochure } from './crm';

export enum Language {
  DE = 'de',
  FR = 'fr',
  HU = 'hu',
  IT = 'it',
  NL = 'nl',
  CS = 'cs',
  PL = 'pl',
  ES = 'es',
  CA = 'ca',
  SK = 'sk',
  RO = 'ro',
  PT = 'pt',
  EN = 'en',
}

export enum Market {
  DE = 'DE',
  FR = 'FR',
  HU = 'HU',
  CH = 'CH',
  IT = 'IT',
  AT = 'AT',
  BE = 'BE',
  CZ = 'CZ',
  PL = 'PL',
  NL = 'NL',
  ES = 'ES',
  SK = 'SK',
  RO = 'RO',
  PT = 'PT',
  GB = 'GB',
}

export const allMarkets = Object.values(Market);
export const allLanguages = Object.values(Language);

export const marketFromString = (s: string): Market => {
  const idx = allMarkets.indexOf(s as Market);
  if (idx >= 0) {
    return allMarkets[idx];
  }

  throw new Error(`Unknown market: ${s}`);
};

export const languageFromString = (s: string): Language => {
  const idx = allLanguages.indexOf(s as Language);
  if (idx >= 0) {
    return allLanguages[idx];
  }

  throw new Error(`Unknown language: ${s}`);
};

export type SocialMediaLinks = Partial<{
  facebook: string;
  instagram: string;
  kununu: string;
  linkedin: string;
  x: string;
  xing: string;
  youtube: string;
  blog: string;
}>;

export type StrapiMarketLanguageConfig = {
  localizedMarketName: string;
  localizedLanguageLabel?: string; // only for multi-language markets, for example in the footer
  customerPortal: string; // Market BE must use localized links, so we put all links here
  careerPortal: string;
  onlineShop?: string;
  whistleblowerSystem: string;
  usercentricsId: string;
  locale: string;
} & SocialMediaLinks;

export type MarketLanguage = {
  code: Language;
  domain: string;
  langPrefix?: string;
  cmsFallback?: Market; // first language of the fallback market is the fallback language for cms content
  excludedServiceSegments?: ServiceSegmentType[];
};

export type MarketData = {
  languages: MarketLanguage[];
  serviceSegments: ServiceSegmentType[];
  hasClothingSegments: boolean;
  countryCallingCode: string;
  maxEmployeeCountForSmallCompany: number;
};

export type MarketsConfig = Record<Market, MarketData>;

type Model = {
  name: string;
  h1: string | null;
  infoText: string | null;
  keyFacts: string[];
};

export type ObjectMap<T> = Record<string, T>;

export type UUID = string;

export type ProductComponent = {
  title: string;
  titleDE: string;
  items: { text: string; assetId: string }[];
};

export type NomenclatureData = {
  name: string;
  productGroupName: string;
  displayName: string;
  displayNameDE: string;
  weightInG: string;
  assetId: string | null;
  model: Model;
  material: string;
  colors: number[][];
  colorName: string | null;
  colorGroupName: string | null;
  components: ProductComponent[];
  equipment: string | null;
  emotions: string[];
  sizes: string[];
  collection: UUID | null;
  colorVariants: UUID[];
  appropriateMarkets: Market[];
  metaTitle: string | null;
  metaDescription: string | null;
  alternatives: AlternativeLink[];
  serviceSegmentUuid: string;
  serviceSegmentKey: ServiceSegmentType;
};

/**
 * The `Nomenclature` represents the Data for a single nomenclature from the
 * PIM cache. This is an intermediate format for the cache and will be
 * transformed to `Product` and `ProductVariant` in the gatsby-pim-source
 * plugin.
 */
export type Nomenclature = NomenclatureData & {
  uuid: string;
};

export type AlternativeLink = {
  locale: string;
  href: string;
};

export type ServiceSegmentCategory =
  | 'MBS'
  | 'MTS'
  | 'MWS'
  | 'MTR'
  | 'MFS'
  | 'MRS';

export const isClothing = (serviceSegment: {
  category: ServiceSegmentCategory;
}) => serviceSegment.category === 'MBS';

export const isCleanroom = (serviceSegment: {
  category: ServiceSegmentCategory;
}) => serviceSegment.category === 'MRS';

export const isNonClothing = (serviceSegment: {
  category: ServiceSegmentCategory;
}) => serviceSegment.category !== 'MBS' && serviceSegment.category !== 'MRS';

export const getCategoryName = (
  serviceSegment: {
    category: ServiceSegmentCategory;
  },
  navigation: NavigationLinks['navigation'],
) =>
  isClothing(serviceSegment)
    ? navigation.clothing.name
    : isCleanroom(serviceSegment)
      ? navigation.cleanroom.name
      : navigation.nonClothing.name;

export type ServiceSegmentBase = {
  name: string;
  metaTitle: string;
  metaDescription: string;
  claim: string;
  category: ServiceSegmentCategory;

  // assets
  assets: {
    default: string | null;
    hover: string | null;

    // only relevant for non-clothing categories
    emotion: string | null;
    keyVisualDesktop: string | null;
    keyVisualMobile: string | null;
  };

  // only relevant for none clothing service segments
  property: string | null;
  infoText1: string | null;
  infoText2: string | null;
  galleryTitle: string | null;
  galleryIntroText: string | null;

  seoFooter: string | null;
  alternatives: AlternativeLink[];
  key: ServiceSegmentType;
  appropriateMarkets: Market[];
};

export type ServiceSegment = ServiceSegmentBase & {
  nomenclatureGroups: {
    uuid: string;
    name: string;
    nomenclatures: UUID[];
  }[];
};

export enum ServiceSegmentType {
  workwear = 'workwear',
  protectiveClothing = 'protectiveClothing',
  hygieneClothing = 'hygieneClothing',
  healthClothing = 'healthClothing',
  businessClothing = 'businessClothing',
  serviceClothing = 'serviceClothing',
  cleanRoomClothing = 'cleanRoomClothing',
  cleanRoomAccessories = 'cleanRoomAccessories',
  cleanRoomCleaning = 'cleanRoomCleaning',
  cleanRoomTrainings = 'cleanRoomTrainings',
  cleaningCloths = 'cleaningCloths',
  washroomHygiene = 'washroomHygiene',
  carCovers = 'carCovers',
  oilCollectionMats = 'oilCollectionMats',
  partsCleaner = 'partsCleaner',
  dirtTrappingMats = 'dirtTrappingMats',
  wipingMops = 'wipingMops',
}

export const allServiceSegments = Object.values(ServiceSegmentType);

export enum ServiceSegmentSubType {
  chemicalProtection = 'chemicalProtection',
  productProtection = 'productProtection',
  foundryProtection = 'foundryProtection',
  glassProtection = 'glassProtection',
  heatAndFlameProtection = 'heatAndFlameProtection',
  hiVisProtection = 'hiVisProtection',
  weatherProtection = 'weatherProtection',
  cementProtection = 'cementProtection',
  coldProtection = 'coldProtection',
}

export type Product = Pick<
  NomenclatureData,
  | 'displayName'
  | 'displayNameDE'
  | 'productGroupName'
  | 'collection'
  | 'metaTitle'
  | 'metaDescription'
  | 'alternatives'
  | 'serviceSegmentKey'
  | 'serviceSegmentUuid'
> & {
  uuid: UUID; // this will be the UUID of the first variant
  h1: NomenclatureData['model']['h1'];
  infoText: NomenclatureData['model']['infoText'];
  keyFacts: NomenclatureData['model']['keyFacts'];
  appropriateMarkets: Market[];
  variants: ProductVariant[];
  modelName: NomenclatureData['model']['name'];
};

export type ProductVariant = Pick<
  NomenclatureData,
  | 'name'
  | 'weightInG'
  | 'assetId'
  | 'material'
  | 'colors'
  | 'colorName'
  | 'colorGroupName'
  | 'components'
  | 'equipment'
  | 'emotions'
  | 'sizes'
> & {
  uuid: UUID;
};

export type BrochureDownloadItem = {
  label: string;
  key: Brochure;
  files: {
    thumbnail: string;
    name: string;
    url: string;
    sizeInKiloBytes: number;
    extension: string;
  }[];
};

export type ServiceSegmentMeta = {
  uuid: string;
  name: string;
  hasServiceSegmentGroups?: boolean; // shows if the service segment contains a 'service segment groups' layer
  children?: Record<UUID, ServiceSegmentSubType>;
};

export type Collection = { uuid: string } & CollectionData;

export type CollectionData = {
  name: string;
  claim: string | null;
  property: string | null;
  description: string | null;
  infoText1: string | null;
  infoText2: string | null;
  keyFacts: string[];
  assets: {
    keyVisualDesktop: string | null;
    keyVisualMobile: string | null;
    emotion: string | null;
  };
  nomenclatures: UUID[];
  alternatives: AlternativeLink[];
  industries: string[];
};

export type Industry = { uuid: string } & IndustryData;

export type IndustryData = {
  name: string;
  translatedSlugs: {
    locale: string;
    slug: string;
  }[];
  h1: string | null;
  h2: string | null;
  infoText: string | null;
  assets: {
    default: string | null;
    keyVisualClothingDesktop: string | null;
    keyVisualClothingMobile: string | null;
    keyVisualNonClothingDesktop: string | null;
    keyVisualNonClothingMobile: string | null;
  };
  serviceSegments: {
    uuid: string;
    name: string;
    nomenclatureGroups: {
      uuid: string;
      name: string;
      nomenclatures: UUID[];
    }[];
    alternatives: AlternativeLink[];
    appropriateMarkets: Market[];
    category: ServiceSegmentCategory;
    key: ServiceSegmentType;
  }[];
};

export enum ServiceTeaserType {
  logoEmbroideryService = 'logo-stick-service',
  cleaningClothsServiceTest = 'putztuch-service-test',
}

export type ServiceTeaserData<T extends ServiceTeaserType> = {
  title: string;
  description: string;
  serviceType: T;
};

export type LinkData = {
  name: string;
  slug: string;
  isExternal?: boolean;
  handleClick?: () => void;
};

export type SubMenu = {
  title: string;
  slug: string;
  links: LinkData[];
};

export type StrapiSinglePages<Payload = LinkData> = {
  imprint: Payload;
  privacyPolicy: Payload;
  privacyPolicySocialMedia: Payload;
  privacyPolicyApplicant: Payload;
  privacyPolicyBusinessContact: Payload;
  privacyPolicySurvey: Payload;
  supplyChainAct: Payload;
  surveyVoiceOfCustomer: Payload;
  pressOverview: Payload;
  fullService: Payload;
  clothingCategory: Payload;
  industryCategory: Payload;
  nonClothingCategory: Payload;
  cleanroomCategory: Payload;
  company: Payload;
  companyHomepage: Payload;
  companySustainability: Payload;
  companySponsoring: Payload;
  companyLocation: Payload;
  companyHistory: Payload;
  clothServiceTestPage: Payload;
  clothServiceTestForm: Payload;
  brochuresDownload: Payload;
  contact: Payload;
};

export type StrapiLegalPages<Payload = LinkData> = {
  name: string;
  items: Payload[];
}[];

export type NavigationLinks<Payload = LinkData> = {
  navigation: {
    productMenu: Payload;
    nonClothing: Payload;
    clothing: Payload;
    cleanroom: Payload;
    industries: Payload;
    company: Payload;
  };
  conversion: {
    contact: Payload;
    brochuresDownload: Payload;
    cleaningClothsServiceTestForm: Payload;
    cleaningClothsServiceTestPage: Payload;
  };
  fullService: Payload;
  searchResultPage: Payload;
  blog: {
    generalSlug: Payload;
    topicSlug: Payload;
    authorSlug: Payload;
  };
  legal: (Payload & { typeName: string })[];
  imprint: Payload;
  privacyPolicy: Payload;
  privacyPolicySocialMedia: Payload;
  privacyPolicyApplicant?: Payload;
  privacyPolicySurvey: Payload;
  privacyPolicyBusinessContact?: Payload;
  supplyChainAct?: Payload;
  surveyVoiceOfCustomer?: Payload;
  pressOverview: Payload;
  companyHomepage: Payload;
  companySustainability: Payload;
  companySponsoring: Payload;
  companyLocation: Payload;
  companyHistory: Payload;
};

export enum MewaAdvantagesIcon {
  MbsWideRange = 'mbs-wide-range',
  MbsIndividualEquipment = 'mbs-individual-equipment',
  MbsNoRiskOfConfusion = 'mbs-no-risk-of-confusion',
  MbsInYourBrandDesign = 'mbs-in-your-brand-design',
  MbsFlawlessHygiene = 'mbs-flawless-hygiene',
  MbsCarefreePackage = 'mbs-carefree-package',
  MbsPureRelief = 'mbs-pure-relief',
  MbsSecureSupplyInYourArea = 'mbs-secure-supply-in-your-area',
  MbsSustainabilityInclusive = 'mbs-sustainability-inclusive',
  MbsComfortable = 'mbs-comfortable',

  MtsGreatVariety = 'mts-great-variety',
  MtsQualityGuaranteed = 'mts-quality-guaranteed',
  MtsPureRelief = 'mts-pure-relief',
  MtsSecureSupplyInYourArea = 'mts-secure-supply-in-your-area',
  MtsSustainabilityInclusive = 'mts-sustainability-inclusive',
  MtsCarefreePackage = 'mts-carefree-package',
  MtsComfortable = 'mts-comfortable',

  CostSaving = 'cost-saving',
  Sustainable = 'sustainable',
  Certificate = 'certificate',

  TimeSaving = 'time-saving',
  Norms = 'norms',
}

export enum MewaStepsIcon {
  CalendarMbs = 'calendar-mbs',
  CalendarMts = 'calendar-mts',
  TruckMbs = 'truck-mbs',
  TruckMts = 'truck-mts',
  CleanAndMaintain = 'clean-and-maintain',
  Truck = 'truck',
  Droplet = 'droplet',
  MbsSecureSupplyInYourArea = 'mbs-secure-supply-in-your-area',
  PreparationDin14644 = 'preparation-din-14644',
  HeadProtection = 'head-protection',
  MrsCircularClothing = 'mrs-circular-clothing',
}

export enum MewaCircleIcon {
  CategoryMbsMtsCombined = 'category-mbs-mts-combined', // default
  CategoryMbs = 'category-mbs', // Berufsbekleidung
  CategoryMts = 'category-mts', // Tuchservice
  CategoryMbk = 'category-mbk', // Businesskleidung
  CategoryMfs = 'category-mfs', // Fußmatten
  CategoryMtr = 'category-mtr', // Teilereiniger
  CategoryMultitex = 'category-multitex', // Ölauffangmatten
  CategoryMws = 'category-mws', // Waschraum
  CategoryToptex = 'category-toptex', // KFZ-Schonbezüge
  CategoryWipingmops = 'category-wipingmops', // Wischmopps

  // General icons
  Truck = 'truck',
  Magnifier = 'magnifier',
  Droplet = 'droplet',

  // Special icons for MTR
  MtrCleaning = 'mtr-cleaning',
  MtrChangeLiquid = 'mtr-change-liquid',
  MtrExchange = 'mtr-exchange',
  MtrImmediateHelp = 'mtr-immediate-help',

  // Special icons for clean room
  HeadProtection = 'head-protection',
  PreparationDin14644 = 'preparation-din-14644',
}

export type MewaCircleType =
  | 'default'
  | 'MBS'
  | 'MTS'
  | 'MBK'
  | 'MFS'
  | 'MTR'
  | 'Multitex'
  | 'MWS'
  | 'Toptex'
  | 'Wipingmops'
  | 'CleanRoom';

export enum PageType {
  productDetail = 'productDetail',
  serviceSegment = 'serviceSegment',
  pressRelease = 'pressRelease',
  pressOverview = 'pressOverview',
  blogArticle = 'blogArticle',
  blogOverview = 'blogOverview',
  blogAuthor = 'blogAuthor',
  blogTopic = 'blogTopic',
  collection = 'collection',
  industry = 'industry',
  company = 'company',
  fullService = 'fullService',
  mewaPrinciple = 'mewaPrinciple',
  campaign = 'campaign',
  conversion = 'conversion',
  cleaningClothsServiceTest = 'cleaningClothsServiceTest',
  home = 'home',
  legal = 'legal',
  meta = 'meta',
  search = 'search',
  supplyChainAct = 'supplyChainAct',
  category = 'category',
  productOverview = 'productOverview',
}

export const allPageType = Object.values(PageType);

export enum LegalPageType {
  imprint = 'imprint',
  privacyPolicy = 'privacyPolicy',
  privacyPolicySurvey = 'privacyPolicySurvey',
  privacyPolicySocialMedia = 'privacyPolicySocialMedia',
  privacyPolicyApplicant = 'privacyPolicyApplicant',
  privacyPolicyBusinessContact = 'privacyPolicyBusinessContact',
  supplyChainAct = 'supplyChainAct',
  surveyVoiceOfCustomer = 'surveyVoiceOfCustomer',
}

export type TextWithMarking = {
  beforeMarking: string;
  marking: string;
  afterMarking: string;
};

export type MediaGallerySlideImage = {
  aspectRatio: string;
  width: number;
  marginTop: number;
  marginLeft?: number;
};

export type MediaGallerySlideConfig = {
  left: MediaGallerySlideImage;
  topRight: MediaGallerySlideImage;
  bottomRight: MediaGallerySlideImage;
};

export enum MediaQuery {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
}

export type MediaQueryData<T> = Record<MediaQuery, T>;

export const multimediaGallerySlides: MediaQueryData<
  MediaGallerySlideConfig[]
> = {
  xs: [
    {
      left: {
        marginTop: 0,
        aspectRatio: '220 / 236',
        width: 220 / 330,
      },
      topRight: {
        marginLeft: 109.23 / 219.33,
        marginTop: 123 / 219.33,
        aspectRatio: '110 / 113',
        width: 110 / 330,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '219.23 / 111',
        width: 219.33 / 330,
      },
    },
    {
      left: {
        marginTop: 85 / 110,
        aspectRatio: '110 / 139',
        width: 110 / 330,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '110 / 135',
        width: 110 / 330,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '220 / 212',
        width: 220 / 330,
      },
    },
    {
      left: {
        marginTop: 138 / 110.64,
        aspectRatio: '110.64 / 116',
        width: 110.64 / 330,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '220 / 220',
        width: 220 / 330,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '175 / 127',
        width: 175 / 330,
      },
    },
    {
      left: {
        marginTop: 0,
        aspectRatio: '220 / 347',
        width: 220 / 330,
      },
      topRight: {
        marginTop: 118 / 110,
        aspectRatio: '110 / 100',
        width: 110 / 330,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '110 / 100',
        width: 110 / 330,
      },
    },
  ],
  sm: [
    {
      left: {
        marginTop: 40 / 450,
        aspectRatio: '450 / 459',
        width: 450 / 750,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '300 / 292',
        width: 300 / 750,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '300 / 152',
        width: 300 / 750,
      },
    },
    {
      left: {
        marginTop: 80 / 300,
        aspectRatio: '300 / 279',
        width: 300 / 750,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '170 / 144',
        width: 170 / 750,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '450 / 356',
        width: 450 / 750,
      },
    },
    {
      left: {
        marginTop: 183 / 200.29,
        aspectRatio: '200.29 / 210',
        width: 200.29 / 750,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '550 / 320',
        width: 550 / 750,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '425 / 180',
        width: 425 / 750,
      },
    },
    {
      left: {
        marginTop: 0,
        aspectRatio: '550 / 500',
        width: 550 / 750,
      },
      topRight: {
        marginTop: 113 / 200,
        aspectRatio: '200 / 220',
        width: 200 / 750,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '174.87 / 117',
        width: 174.87 / 750,
      },
    },
  ],
  md: [
    {
      left: {
        marginTop: 69 / 752.25,
        aspectRatio: '752.25 / 572.25',
        width: 752.25 / 1290.2,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '537.75 / 355.5',
        width: 537.75 / 1290.2,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '474 / 240',
        width: 474 / 1290.2,
      },
    },
    {
      left: {
        marginTop: 42 / 492.75,
        aspectRatio: '492.75 / 371.79',
        width: 492.75 / 1290.2,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '227.15 / 215.25',
        width: 227.15 / 1290.2,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '797.25 / 425.98',
        width: 797.25 / 1290.2,
      },
    },
    {
      left: {
        marginTop: 133.5 / 417.75,
        aspectRatio: '417.75 / 438',
        width: 417.75 / 1290.2,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '872.25 / 420',
        width: 872.25 / 1290.2,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '465.75 / 222',
        width: 465.75 / 1290.2,
      },
    },
    {
      left: {
        marginTop: 0,
        aspectRatio: '907.5 / 641.25',
        width: 907.5 / 1290.2,
      },
      topRight: {
        marginTop: 66.75 / 382.5,
        aspectRatio: '382.5 / 366.75',
        width: 382.5 / 1290.2,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '310.5 / 207.75',
        width: 310.5 / 1290.2,
      },
    },
  ],
  lg: [
    {
      left: {
        marginTop: 92 / 1003,
        aspectRatio: '1003 / 763',
        width: 1003 / 1720.27,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '717 / 474',
        width: 717 / 1720.27,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '632 / 320',
        width: 632 / 1720.27,
      },
    },
    {
      left: {
        marginTop: 56 / 657,
        aspectRatio: '657 / 495',
        width: 657 / 1720,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '302 / 287',
        width: 302 / 1720,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '1063 / 567',
        width: 1063 / 1720,
      },
    },
    {
      left: {
        marginTop: 178 / 557,
        aspectRatio: '557 / 584',
        width: 557 / 1720,
      },
      topRight: {
        marginTop: 0,
        aspectRatio: '1163 / 560',
        width: 1163 / 1720,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '621 / 296',
        width: 621 / 1720,
      },
    },
    {
      left: {
        marginTop: 0,
        aspectRatio: '1210 / 855',
        width: 1210 / 1720,
      },
      topRight: {
        marginTop: 89 / 510,
        aspectRatio: '510 / 489',
        width: 510 / 1720,
      },
      bottomRight: {
        marginTop: 0,
        aspectRatio: '414 / 277',
        width: 414 / 1720,
      },
    },
  ],
};
